import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { GeminiAPIResponse } from '../../types';
import { useTheme } from './ThemeContext';

interface TextResultBoxProps {
    apiResponse: GeminiAPIResponse | null;
    selectedLanguage: string;
    isLoading?: boolean;
}

const TextResultBox: React.FC<TextResultBoxProps> = ({ apiResponse, selectedLanguage, isLoading = false }) => {
    const [text, setText] = useState<string>('No response text available');
    const [translatedText, setTranslatedText] = useState<string>('');
    const [isTranslating, setIsTranslating] = useState<boolean>(false);
    const [translationError, setTranslationError] = useState<string | null>(null);
    const { theme } = useTheme();

    useEffect(() => {
        if (apiResponse && apiResponse.candidates && apiResponse.candidates.length > 0) {
            const candidate = apiResponse.candidates[0];
            if (candidate.content && candidate.content.parts && candidate.content.parts.length > 0) {
                setText(candidate.content.parts[0].text || 'Response text is empty');
            }
        }
    }, [apiResponse]);

    useEffect(() => {
        const translateText = async (): Promise<void> => {
            if (selectedLanguage && selectedLanguage !== 'English' && text) {
                setIsTranslating(true);
                setTranslationError(null);
                // ... (rest of translation logic)
                try {
                    const response = await axios.post(
                        'https://libretranslate.de/translate',
                        {
                            q: text,
                            source: 'en',
                            target: getLanguageCode(selectedLanguage),
                        },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                            },
                        }
                    );
                    setTranslatedText(response.data.translatedText);
                } catch (error) {
                    console.error('Translation error:', error);
                    setTranslationError('An error occurred during translation. Please try again.');
                } finally {
                    setIsTranslating(false);
                }
            } else {
                setTranslatedText('');
            }
        };

        if (text && text !== 'No response text available') {
            translateText();
        }
    }, [selectedLanguage, text]);

    const getLanguageCode = (language: string): string => {
        const languageCodes: { [key: string]: string } = {
            'English': 'en',
            'Hindi': 'hi',
            'Marathi': 'mr',
            'Telugu': 'te',
            'Tamil': 'ta',
            'Kannada': 'kn',
        };
        return languageCodes[language] || 'en';
    };

    const displayText = translatedText || text;

    return (
        <div className={`text-justify p-6 max-w-4xl mx-auto border rounded-lg shadow-md ${theme === 'dark'
            ? 'bg-[#1e2128] border-gray-700 text-gray-200'
            : 'bg-white border-gray-200 text-gray-900'
            }`}>
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
                    <p className="text-sm opacity-70">Generating response...</p>
                </div>
            ) : (
                <>
                    {isTranslating && (
                        <div className="mb-4 text-blue-500">Translating to {selectedLanguage}...</div>
                    )}
                    {translationError && (
                        <div className="mb-4 text-red-500">{translationError}</div>
                    )}
                    <div className={`prose prose-sm max-w-none ${theme === 'dark' ? 'prose-invert' : ''}`}>
                        <ReactMarkdown>{displayText}</ReactMarkdown>
                    </div>
                    {apiResponse && apiResponse.error && (
                        <div className="text-red-500 mt-4">Error: {apiResponse.error}</div>
                    )}
                </>
            )}
        </div>
    );
};

export default TextResultBox;